import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Check, Leaf, ArrowRight, Package, Recycle, Heart } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Licenses from '../components/Licenses';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--cream))] grain-texture">
      <Navbar />

      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 pt-32 pb-16">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Copy */}
            <div className="space-y-8 animate-slide-in-up">
              <div className="inline-block">
                <span className="bg-[hsl(var(--leaf-light))] text-[hsl(var(--leaf-dark))] px-4 py-2 rounded-full text-sm font-accent font-semibold uppercase tracking-wider">
                  Biodegrades in 180 days
                </span>
              </div>

              <h1 className="font-heading text-5xl lg:text-7xl text-[hsl(var(--charcoal))] leading-[0.95]">
                Nature's<br />
                Answer to<br />
                <span className="text-[hsl(var(--earth-green))] italic">Plastic</span>
              </h1>

              <p className="font-body text-lg text-[hsl(var(--stone-gray))] leading-relaxed max-w-md">
                Premium corn-based bags that perform like plastic but disappear like nature intended. Beautiful, functional, and guilt-free.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/products"
                  data-testid="hero-shop-button"
                  className="btn-primary bg-[hsl(var(--earth-green))] text-[hsl(var(--cream))] px-8 py-4 rounded-2xl font-accent font-semibold uppercase tracking-wider shadow-lg inline-flex items-center justify-center gap-2"
                >
                  Shop Collection
                  <ArrowRight className="w-5 h-5" />
                </Link>

                <a
                  href="#sustainability"
                  className="bg-transparent text-[hsl(var(--earth-green))] border-2 border-[hsl(var(--earth-green))] px-8 py-4 rounded-2xl font-accent font-semibold uppercase tracking-wider hover:bg-[hsl(var(--earth-green))] hover:text-[hsl(var(--cream))] transition-all duration-300 inline-flex items-center justify-center"
                >
                  Learn More
                </a>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-6 pt-8">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[hsl(var(--leaf-medium))] rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-[hsl(var(--earth-green))]" />
                  </div>
                  <span className="text-sm font-body text-[hsl(var(--stone-gray))]">
                    100% Compostable
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[hsl(var(--leaf-medium))] rounded-full flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-[hsl(var(--earth-green))]" />
                  </div>
                  <span className="text-sm font-body text-[hsl(var(--stone-gray))]">
                    Carbon Neutral
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Logo */}
            <div className="relative animate-fade-in flex items-center justify-center" style={{ animationDelay: '0.2s' }}>
              <div className="bg-gradient-to-br from-[hsl(var(--sand))]/50 to-[hsl(var(--leaf-light))]/30 rounded-3xl p-12 backdrop-blur-sm w-full flex items-center justify-center">
                <img 
                  src="/logo.svg"
                  alt="Easecarry Green Logo"
                  className="w-full max-w-lg h-auto animate-float"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-[hsl(var(--stone-gray))]" />
        </div>
      </section>

      {/* Features Section */}
      <section id="sustainability" className="py-24 bg-[hsl(var(--pure-white))]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl text-[hsl(var(--charcoal))] mb-4">
              Why Choose <span className="text-[hsl(var(--earth-green))] italic">Green</span>?
            </h2>
            <p className="font-body text-lg text-[hsl(var(--stone-gray))] max-w-2xl mx-auto">
              Our bags combine cutting-edge materials science with timeless design
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div data-testid="feature-compostable" className="bg-[hsl(var(--leaf-light))]/50 rounded-3xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[hsl(var(--earth-green))] rounded-2xl flex items-center justify-center mb-6">
                <Recycle className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl text-[hsl(var(--charcoal))] mb-3">
                100% Compostable
              </h3>
              <p className="font-body text-[hsl(var(--stone-gray))] leading-relaxed">
                Made from corn starch, our bags break down naturally in 180 days, leaving zero microplastics behind.
              </p>
            </div>

            <div data-testid="feature-durable" className="bg-[hsl(var(--cream))] rounded-3xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[hsl(var(--earth-green))] rounded-2xl flex items-center justify-center mb-6">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl text-[hsl(var(--charcoal))] mb-3">
                Strong & Durable
              </h3>
              <p className="font-body text-[hsl(var(--stone-gray))] leading-relaxed">
                Don't compromise on quality. Our bags are tear-resistant and water-proof for everyday use.
              </p>
            </div>

            <div data-testid="feature-beautiful" className="bg-[hsl(var(--leaf-light))]/50 rounded-3xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[hsl(var(--earth-green))] rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl text-[hsl(var(--charcoal))] mb-3">
                Simply Beautiful
              </h3>
              <p className="font-body text-[hsl(var(--stone-gray))] leading-relaxed">
                Available in nature-inspired colors that elevate your everyday carry with style.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Licenses Section */}
      <Licenses />

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-br from-[hsl(var(--earth-green))] to-[hsl(var(--moss-green))] rounded-3xl p-12 lg:p-16 text-center">
            <h2 className="font-heading text-4xl lg:text-5xl text-white mb-6">
              Ready to Make the Switch?
            </h2>
            <p className="font-body text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Join thousands of eco-conscious shoppers choosing sustainable bags that don't sacrifice quality or style.
            </p>
            <Link
              to="/products"
              data-testid="cta-shop-button"
              className="inline-flex items-center gap-2 bg-white text-[hsl(var(--earth-green))] px-10 py-5 rounded-2xl font-accent font-bold uppercase tracking-wider hover:scale-105 hover:shadow-2xl transition-all duration-300"
            >
              Browse Collection
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;